/** @jsxImportSource @emotion/react */
import React, { useCallback, useContext, useRef, useState } from "react";

import ReactFlow, {
  ReactFlowProvider,
  Controls,
  Background,
  addEdge,
  Connection,
  Node,
  ReactFlowInstance,
  Edge,
  OnNodesChange,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { DateObject } from "react-multi-date-picker";
import { debounce } from "lodash";
import { useDrop } from "react-dnd";

import CampaignContext from "./CampaignContext";
import SideMenu from "./SideMenu";
import AuthenticationContext from "../../Authentication/AuthenticationContext";

import CustomButton from "../kits/button/CustomButton";
import { TrashIcon } from "../kits/Icons";
import useAxios from "../../services/useAxios";
import ToastCustomize from "../kits/ToastCustomize";

import {
  InsertStepRequestType,
  InsertResponseType,
  NodeType,
  RemoveNodeRequestType,
  ResponseType,
  UpdateStepRequestType,
} from "./SiCaTypes";
import {
  apiResponseHandler,
  nodeTypeConvertToTitle,
  nodeTypeConvertToNumber,
} from "./SiCahelper";

import { StepTabStyles } from "./SiCaStyles";
import { AllNodes } from "./SiCaData";

interface DropResult {
  name: string;
}

interface StepTabProps {}

const StepTab: React.FC<StepTabProps> = () => {
  const { mainDetails, nodes, edges, changeEdges, changeNodes, onEdgesChange } =
    useContext(CampaignContext);
  const { userInfo } = useContext(AuthenticationContext);

  const { execute: InsertStepAPI } = useAxios("/api/Insert_Step");
  const { execute: UpdateStepAPI } = useAxios("/api/Update_Step");
  const { execute: RemoveNodeAPI } = useAxios("/api/remove_node");

  const updatePosition = useRef(
    debounce((data) => {
      UpdateStepAPI(data).then((res: ResponseType) => {
        if (res.status !== 1) {
          ToastCustomize({
            mainText:
              "خطایی در ارتباط با سرور بوجود آمده است. لطفا صفحه را رفرش کنید.",
            duration: 2000,
            toastType: "failed",
            color: "danger",
          });
        }
      });
    }, 500)
  );

  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance>();

  const [, drop] = useDrop<DropResult>(
    () => ({
      accept: "Box",
      drop: (item, monitor) => {
        const reactFlowBounds =
          reactFlowWrapper?.current?.getBoundingClientRect();
        const type = item.name;
        const offsets = monitor.getSourceClientOffset();

        console.log(type);
        console.log(reactFlowBounds);
        console.log(reactFlowInstance);
        console.log(offsets);

        if (
          typeof type === "undefined" ||
          !type ||
          typeof reactFlowBounds === "undefined" ||
          !reactFlowBounds ||
          typeof reactFlowInstance === "undefined" ||
          !reactFlowInstance ||
          offsets === null
        ) {
          return;
        }

        const position = reactFlowInstance.project({
          x: offsets.x - reactFlowBounds.left,
          y: offsets.y - reactFlowBounds.top,
        });

        const numericType = nodeTypeConvertToNumber(type);

        const nodeId = Date.now().toString().slice(0, -3);
        const newNode: Node<NodeType> = {
          id: nodeId,
          type,
          position,
          data: {
            id: +nodeId,
            title: nodeTypeConvertToTitle(+numericType),
            description: "",
            time: 0,
            automate: false,
            text: "",
            isActive: true,
            type: +numericType,
            trueLabel: "موفق",
            falseLabel: "ناموفق",
          },
        };

        const data: InsertStepRequestType = {
          campaign_id: mainDetails.id,
          status: "2",
          title: newNode.data.title,
          position_x: "" + newNode.position.x,
          position_y: "" + newNode.position.y,
          type: "" + numericType,
          true_label: "موفق",
          false_label: "ناموفق",
          mtm: "" + new DateObject().unix,
          uid: "" + userInfo.id,
        };

        InsertStepAPI(data).then((res: InsertResponseType) => {
          if (Boolean(res.status)) {
            newNode.id = "" + +res.result.id;
            newNode.data.id = +res.result.id;
            changeNodes(nodes.concat(newNode));
          } else {
            ToastCustomize({
              mainText:
                "مشکلی در ایجاد ارتباط رخ داده است. لطفا مجددا تلاش کنید.",
              duration: 2000,
              toastType: "failed",
              color: "danger",
            });
          }
        });
      },
      collect: (monitor) => ({}),
    }),
    [
      reactFlowInstance,
      mainDetails.id,
      userInfo.id,
      reactFlowWrapper,
      nodes,
      changeNodes,
    ]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      let edge: Edge = {
        id: Date.now().toString().slice(0, -3),
        source: connection.source!,
        sourceHandle: connection.sourceHandle,
        target: connection.target!,
        targetHandle: connection.targetHandle,
      };

      if (
        edges.findIndex(
          (ed) =>
            ed.source === edge.source && ed.sourceHandle === edge.sourceHandle
        ) !== -1
      ) {
        ToastCustomize({
          mainText:
            "نمی‌توانید بیشتر از یک ارتباط از یک نقطه خروجی در گره ایجاد کنید.",
          duration: 2000,
          toastType: "failed",
          color: "danger",
        });
        return;
      }

      const node = nodes.find((node) => node.id === connection.source);

      // if don't find node
      if (!node) return;

      let keyvalues = "";

      // set edge label with custom text
      if (connection.sourceHandle === "true") {
        // edge.label = "موفق";
        edge.label = node.data.trueLabel;
        keyvalues = `status_true,${connection.target}`;
      } else if (connection.sourceHandle === "false") {
        // edge.label = "ناموفق";
        edge.label = node.data.falseLabel;
        keyvalues = `status_false,${connection.target}`;
      } else {
        keyvalues = `status_true,${connection.target}`;
      }

      keyvalues += `@@uptm,${new DateObject().unix}@@upuid,${userInfo.id}`;
      const data: UpdateStepRequestType = {
        conditions: `id/=/${connection.source}@@campaign_id/=/${mainDetails.id}`,
        keyvalues,
      };

      UpdateStepAPI(data).then((res: ResponseType) => {
        apiResponseHandler(res, {
          failure: "مشکلی در ایجاد ارتباط رخ داده است. لطفا مجددا تلاش کنید.",
        });

        if (+res.status !== 1) {
          changeEdges(edges.filter((ed) => edge.id !== ed.id));
        }
      });

      changeEdges(addEdge(edge, edges));
    },
    [edges, nodes, mainDetails.id, userInfo.id, changeEdges]
  );

  const onDragOver = useCallback<React.DragEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    []
  );

  const removeNodeOrEdge = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    async (event) => {
      event.preventDefault();

      let tempEdge = edges.find((edge) => edge.selected);
      // edge is selected.
      if (tempEdge) {
        const data: UpdateStepRequestType = {
          conditions: `id/=/${tempEdge.source}`,
          keyvalues: `${
            tempEdge.sourceHandle === "false" ? "status_false" : "status_true"
          },0@@uptm,${new DateObject().unix}@@upuid,${userInfo.id}`,
        };
        UpdateStepAPI(data).then((res: ResponseType) => {
          apiResponseHandler(res, {
            success: "ارتباط با موفقیت حذف شد",
            failure:
              "مشکلی در حذف ارتباط بوجود آمده است، لطفا مجددا تلاش کنید.",
          });
          if (+res.status === 1) {
            changeEdges(edges.filter((ed) => tempEdge?.id !== ed.id));
          }
        });
        return;
      }

      // node is selected
      let tempNode = nodes.find((node) => node.selected);
      if (!tempNode) {
        return;
      }

      // edges which have no connection to/from the selected node
      let tempEdges = edges.filter(
        (edge) =>
          !["" + edge.source, "" + edge.target].includes("" + tempNode?.id)
      );

      const data: RemoveNodeRequestType = {
        node_id: tempNode.id,
      };

      RemoveNodeAPI(data).then((res: ResponseType) => {
        apiResponseHandler(res, {
          success: "گره با موفقیت حذف شد.",
          failure: "مشکلی در حذف گره بوجود آمده است، لطفا مجددا تلاش کنید.",
        });
        if (+res.status === 1) {
          // also we can set nodes and edges from api(it returns)
          changeNodes(nodes.filter((node) => node.id !== tempNode?.id));
          changeEdges(tempEdges);
        }
      });
    },
    [edges, nodes, userInfo.id, changeEdges, changeNodes]
  );

  const onNodesChange = useCallback<OnNodesChange>(
    (nds) => {
      if (nds[0].type === "position" && nds[0].dragging) {
        const data: UpdateStepRequestType = {
          conditions: `id/=/${nds[0].id}`,
          keyvalues: `position_x,${nds[0].position?.x}@@position_y,${nds[0].position?.y}`,
        };
        updatePosition.current(data);
      }
      changeNodes(applyNodeChanges(nds, nodes));
    },
    [nodes, changeNodes]
  );
  return (
    <>
      <div className="row gx-md-2 py-3" css={StepTabStyles}>
        <ReactFlowProvider>
          <div className="col-12 col-md-2 mb-3 mb-md-0">
            <SideMenu />
          </div>
          <div
            dir="ltr"
            className="main-container col-12 col-md-10 pe-md-5"
            style={{ height: "84vh" }}
            ref={reactFlowWrapper}
          >
            <ReactFlow
              {...{
                nodes,
                edges,
                onNodesChange,
                onEdgesChange,
                onConnect,
                nodeTypes: AllNodes,
                onDragOver,
              }}
              ref={drop}
              selectionKeyCode={null}
              deleteKeyCode={null}
              onInit={setReactFlowInstance}
            >
              <Controls />
              <Background />
            </ReactFlow>
            <CustomButton
              className="trash-button"
              color="info"
              type="label"
              onClick={removeNodeOrEdge}
            >
              <TrashIcon />
            </CustomButton>
          </div>
        </ReactFlowProvider>
      </div>
    </>
  );
};

export default StepTab;
